import type { Account } from '@frontendlib/common/types/account';
import { SubscriptionStatus } from '@sharedlib/common/types/accounts/subscription-status';

const REASONS: ReadonlyArray<PendingChargeReason> = ['trial_ended', 'past_due', 'trial_upgrade'];

const STATUSES: ReadonlyArray<string> = Object.values(SubscriptionStatus);

export type PendingChargeReason = 'trial_ended' | 'past_due' | 'trial_upgrade';

export interface PendingChargeSnapshot {
  readonly status: SubscriptionStatus;
  readonly subscriptionPlanId: string | null;
}

/** The account as it stood before the call that starts a charge. */
export interface PendingChargeOrigin extends PendingChargeSnapshot {
  readonly accountId: string;
}

export interface PendingCharge {
  readonly reason: PendingChargeReason;
  readonly planTitle?: string;
  readonly snapshot: PendingChargeSnapshot;
  readonly expiresAt: number;
  readonly expired?: boolean;
}

export function recoveryReason(status: SubscriptionStatus | undefined): PendingChargeReason | null {
  switch (status) {
    case SubscriptionStatus.TRIAL_ENDED:
      return 'trial_ended';
    case SubscriptionStatus.PAST_DUE:
      return 'past_due';
    default:
      return null;
  }
}

export function snapshotOf(origin: PendingChargeOrigin): PendingChargeSnapshot {
  return { status: origin.status, subscriptionPlanId: origin.subscriptionPlanId };
}

// Card-only changes such as `isPaymentMethodSet` do not count: the charge has not landed until one of these moves.
export function differs(account: Account, snapshot: PendingChargeSnapshot): boolean {
  return (
    account.subscriptionStatus !== snapshot.status ||
    (account.subscriptionPlanId ?? null) !== snapshot.subscriptionPlanId
  );
}

function isSubscriptionStatus(value: unknown): value is SubscriptionStatus {
  return typeof value === 'string' && STATUSES.includes(value);
}

function isReason(value: unknown): value is PendingChargeReason {
  return REASONS.some((reason) => reason === value);
}

export function isPendingCharge(raw: unknown): raw is PendingCharge {
  if (typeof raw !== 'object' || raw === null || !('reason' in raw) || !('snapshot' in raw) || !('expiresAt' in raw)) {
    return false;
  }
  const snapshot = raw.snapshot;

  return (
    isReason(raw.reason) &&
    typeof raw.expiresAt === 'number' &&
    typeof snapshot === 'object' &&
    snapshot !== null &&
    'status' in snapshot &&
    isSubscriptionStatus(snapshot.status) &&
    'subscriptionPlanId' in snapshot &&
    (snapshot.subscriptionPlanId === null || typeof snapshot.subscriptionPlanId === 'string')
  );
}
